/**
 * types.ts — Roamer Intelligence Engine™ shared contracts.
 *
 * Everything the engine produces is EXPLAINABLE (carries reasons) and
 * CALCULATED (no hardcoded scores). These types are provider-agnostic so any
 * real API can be swapped in behind the provider interfaces later.
 */

import type { RoamerProfile } from './RoamerProfile';
import type { RoamerLocation } from '@/services/RoamerLocationEngine';

// ─── Recommendation categories ────────────────────────────────────────────────

export type RecommendationCategory =
  | 'housing'
  | 'coworking'
  | 'cafe'
  | 'restaurant'
  | 'neighborhood'
  | 'transportation'
  | 'utility'
  | 'emergency'
  | 'community';

export const RECOMMENDATION_CATEGORIES: RecommendationCategory[] = [
  'housing', 'coworking', 'cafe', 'restaurant', 'neighborhood',
  'transportation', 'utility', 'emergency', 'community',
];

export const RECOMMENDATION_CATEGORY_LABEL: Record<RecommendationCategory, string> = {
  housing: 'Housing',
  coworking: 'Coworking',
  cafe: 'Cafés',
  restaurant: 'Restaurants',
  neighborhood: 'Neighborhoods',
  transportation: 'Transportation',
  utility: 'Utilities',
  emergency: 'Emergency',
  community: 'Community',
};

// ─── Recommendation attributes ────────────────────────────────────────────────
// A superset of measurable facts a provider MIGHT know. All optional — scoring
// only counts the attributes that are present (and reports its confidence).

export interface RecommendationAttributes {
  internetMbps?: number;
  /** Recurring monthly cost in USD (housing / coworking memberships). */
  monthlyCost?: number;
  /** Casual price level 1 (cheap) – 4 (splurge) for food/drink. */
  priceLevel?: 1 | 2 | 3 | 4;
  /** Aggregate community rating 0–5. */
  rating?: number;
  reviewCount?: number;
  /** 0–100 — higher is quieter. */
  quietness?: number;
  /** Walking distance from the base centre, metres. */
  walkabilityMeters?: number;
  /** Straight-line distance from the base, km. */
  distanceKm?: number;
  /** 0–100 neighbourhood safety. */
  safety?: number;
  /** Distance to nearest coworking, metres. */
  nearCoworkingMeters?: number;
  hasAirConditioning?: boolean;
  hasPowerOutlets?: boolean;
  petFriendly?: boolean;
  open24h?: boolean;
  longStayFriendly?: boolean;
  /** 0–100 lifestyle signals for neighbourhoods. */
  nightlife?: number;
  natureAccess?: number;
  beachAccessKm?: number;
  transitScore?: number;
  /** 0–100 — how easy it is to get by in English. */
  englishFriendly?: number;
  amenities?: string[];
}

// ─── Match Score (per recommendation) ─────────────────────────────────────────

export type ReasonPolarity = 'positive' | 'neutral' | 'concern';

export interface MatchReason {
  label: string;
  polarity: ReasonPolarity;
  /** Relative weight this reason carried in the score. */
  weight: number;
}

export interface MatchScore {
  /** 0–100 Roamer Match Score™. */
  score: number;
  /** Top positive drivers — the "Why?" list. */
  reasons: MatchReason[];
  /** Notable downsides worth surfacing. */
  concerns: MatchReason[];
  /** 0–100 — share of scoring weight backed by known attributes. */
  confidence: number;
}

// ─── Recommendation ───────────────────────────────────────────────────────────

export interface Recommendation {
  id: string;
  category: RecommendationCategory;
  title: string;
  subtitle?: string;
  description?: string;
  attributes: RecommendationAttributes;
  /** Human-readable feature tags derived from attributes. */
  tags: string[];
  location: { latitude: number; longitude: number };
  /** Which provider produced this (provenance / plug-and-play tracing). */
  providerId: string;
  /** Attached by the engine after ranking. */
  match?: MatchScore;
}

// ─── Roamer Score (per city) ──────────────────────────────────────────────────

export type RoamerScoreCategory =
  | 'internet'
  | 'housing'
  | 'affordability'
  | 'safety'
  | 'healthcare'
  | 'transportation'
  | 'weather'
  | 'community'
  | 'remoteWork'
  | 'food'
  | 'lifestyle'
  | 'entertainment'
  | 'cost';

export const ROAMER_SCORE_CATEGORIES: RoamerScoreCategory[] = [
  'internet', 'housing', 'affordability', 'safety', 'healthcare',
  'transportation', 'weather', 'community', 'remoteWork', 'food',
  'lifestyle', 'entertainment', 'cost',
];

export const ROAMER_SCORE_CATEGORY_LABEL: Record<RoamerScoreCategory, string> = {
  internet: 'Internet',
  housing: 'Housing',
  affordability: 'Affordability',
  safety: 'Safety',
  healthcare: 'Healthcare',
  transportation: 'Transportation',
  weather: 'Weather',
  community: 'Community',
  remoteWork: 'Remote Work',
  food: 'Food',
  lifestyle: 'Lifestyle',
  entertainment: 'Entertainment',
  cost: 'Overall Cost',
};

/** Raw 0–100 metric per category, produced by a CityMetricsProvider. */
export type CityMetrics = Record<RoamerScoreCategory, number>;

export interface RoamerScoreBreakdownItem {
  category: RoamerScoreCategory;
  label: string;
  /** 0–100 raw metric. */
  score: number;
  /** Normalised weight (0–1) applied to this category. */
  weight: number;
  /** score × weight — the category's contribution to the overall. */
  contribution: number;
}

export type RoamerScoreTier =
  | 'exceptional' | 'excellent' | 'great' | 'good' | 'fair' | 'challenging';

export interface RoamerScore {
  /** 0–100 weighted overall. */
  overall: number;
  breakdown: RoamerScoreBreakdownItem[];
  headline: string;
  tier: RoamerScoreTier;
}

// ─── Daily intelligence ───────────────────────────────────────────────────────

export type DailySuggestionKind =
  | 'weather' | 'internet' | 'currency' | 'coworking'
  | 'market' | 'neighborhood' | 'safety' | 'general';

export interface DailySuggestion {
  id: string;
  kind: DailySuggestionKind;
  /** Lucide icon name hint for the future UI (no rendering here). */
  icon: string;
  text: string;
  detail?: string;
  /** 0–100 — higher shows first. */
  priority: number;
}

// ─── Engine context ───────────────────────────────────────────────────────────

/** Everything the engine needs to answer a query, injected for testability. */
export interface IntelligenceContext {
  location: RoamerLocation;
  profile: RoamerProfile;
  /** Epoch ms — injected so daily/weather logic is deterministic in tests. */
  now: number;
}

export interface RecommendationQueryOptions {
  location?: RoamerLocation;
  profile?: RoamerProfile;
  now?: number;
  limit?: number;
}
